import { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import CertificateOfAuthenticityIcon from '@/assets/all-icons/certificate-of-authenticity-icon.jsx';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useTranslations from '@/i18n/useTranslations';
import {
  getArtworkLimitedEditionOptions,
  isLimitedEditionAvailable,
} from '@/pages/artwork/utils/artworkView.selectors';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import InfoIcon from '@/shared-components/icons/components/public/InfoIcon';
import Popup from '@/shared-components/popups/components/Popup';
import NumberUtils from '@/utils/number.utils';

import SizeOption from './components/SizeOption';
import { FRAME_OPTIONS, MUSEUM_LABEL_OPTIONS } from './constants';

import './index.css';

export default function SelectSize({
  artworkData,
  formState,
  setFormState,
  continueToShipping,
  typeOfPurchase = 'limited_edition',
  showCertificate = true,
}) {
  const t = useTranslations('common.shared.artwork_view.buy_details');
  const [isSizeInfoOpen, setIsSizeInfoOpen] = useState(false);
  const [isFrameInfoOpen, setIsFrameInfoOpen] = useState(false);
  const [isMuseumLabelInfoOpen, setIsMuseumLabelInfoOpen] = useState(false);
  const isLimitedEditionPurchase = typeOfPurchase === 'limited_edition';
  const limitedEditionOptions = getArtworkLimitedEditionOptions(artworkData);
  const isDesktopLayout = useMediaQuery({ query: '(min-width: 901px)' });
  const artworkContext = useContext(ArtworkDataContext);
  const setLimitedEditionPreviewState = artworkContext?.setLimitedEditionPreviewState;
  const artworkId = artworkData?._id ?? null;

  const currency = artworkData?.artwork_about?.artwork_currency;
  const normalizedSizeKey = normalizeSizeKey(formState.limited_edition_size?.size);
  const hasCompletedLimitedEditionSelection = Boolean(formState.limited_edition_size)
    && Boolean(formState.limited_edition_frame)
    && Boolean(formState.limited_edition_museum_label);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsSizeInfoOpen(false);
        setIsFrameInfoOpen(false);
        setIsMuseumLabelInfoOpen(false);
      }
    }

    if (isSizeInfoOpen || isFrameInfoOpen || isMuseumLabelInfoOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSizeInfoOpen, isFrameInfoOpen, isMuseumLabelInfoOpen]);

  useEffect(() => {
    if (typeof setLimitedEditionPreviewState !== 'function') {
      return;
    }

    if (!isLimitedEditionPurchase || !isDesktopLayout) {
      setLimitedEditionPreviewState((prevState) => {
        if (!prevState) {
          return prevState;
        }

        if (prevState.artworkId && prevState.artworkId !== artworkId) {
          return prevState;
        }

        if (!prevState.isVisible && !prevState.selectedSizeKey && !prevState.selectedSizeData) {
          return prevState;
        }

        return {
          ...prevState,
          isVisible: false,
          selectedSizeKey: null,
          selectedSizeData: null,
          selectedFrameKey: null,
          selectedMuseumLabelKey: null,
        };
      });

      return;
    }

    const hasSizeSelection = Boolean(formState.limited_edition_size?.size_data);
    const nextMuseumLabelPreviewKey = hasSizeSelection
      ? (formState.limited_edition_museum_label && formState.limited_edition_museum_label !== 'none'
        ? formState.limited_edition_museum_label
        : null)
      : null;

    // Mirror the size selection on the artwork view so the desktop slider swaps to the staged preview.
    setLimitedEditionPreviewState((prevState) => {
      const previousState = prevState || {};
      const nextState = {
        ...previousState,
        isVisible: hasSizeSelection,
        artworkId: artworkId ?? previousState.artworkId ?? null,
        selectedSizeKey: hasSizeSelection ? formState.limited_edition_size?.size ?? null : null,
        selectedSizeData: hasSizeSelection ? formState.limited_edition_size?.size_data ?? null : null,
        selectedFrameKey: hasSizeSelection ? formState.limited_edition_frame ?? null : null,
        selectedMuseumLabelKey: nextMuseumLabelPreviewKey,
      };

      if (
        nextState.isVisible === previousState.isVisible
        && nextState.selectedSizeKey === previousState.selectedSizeKey
        && nextState.selectedSizeData === previousState.selectedSizeData
        && nextState.selectedFrameKey === previousState.selectedFrameKey
        && nextState.selectedMuseumLabelKey === previousState.selectedMuseumLabelKey
        && nextState.artworkId === previousState.artworkId
      ) {
        return prevState;
      }

      return nextState;
    });
  }, [
    artworkId,
    formState.limited_edition_frame,
    formState.limited_edition_museum_label,
    formState.limited_edition_size,
    isDesktopLayout,
    isLimitedEditionPurchase,
    setLimitedEditionPreviewState,
  ]);

  useEffect(() => () => {
    if (typeof setLimitedEditionPreviewState !== 'function') {
      return;
    }

    setLimitedEditionPreviewState((prevState) => {
      if (!prevState) {
        return prevState;
      }

      if (prevState.artworkId && prevState.artworkId !== artworkId) {
        return prevState;
      }

      if (!prevState.isVisible && !prevState.selectedSizeKey && !prevState.selectedSizeData) {
        return prevState;
      }

      return {
        ...prevState,
        isVisible: false,
        selectedSizeKey: null,
        selectedSizeData: null,
        selectedFrameKey: null,
        selectedMuseumLabelKey: null,
      };
    });
  }, [artworkId, setLimitedEditionPreviewState]);

  function selectLimitedEditionSize(key, value) {
    setFormState((prevState) => {
      const isAlreadySelected = prevState.limited_edition_size?.size === key;
      if (isAlreadySelected) {
        return {
          ...prevState,
          limited_edition_size: null,
          limited_edition_frame_price: getFramePrice(null, prevState.limited_edition_frame),
        };
      }

      const normalizedKey = normalizeSizeKey(key);

      return {
        ...prevState,
        limited_edition_size: {
          size: key,
          size_data: value,
        },
        limited_edition_frame_price: getFramePrice(normalizedKey, prevState.limited_edition_frame),
      };
    });
  }

  function selectFrameOption(optionId) {
    setFormState((prevState) => {
      const nextFrameId = prevState.limited_edition_frame === optionId ? null : optionId;
      const sizeKey = normalizeSizeKey(prevState.limited_edition_size?.size);

      return {
        ...prevState,
        limited_edition_frame: nextFrameId,
        limited_edition_frame_price: getFramePrice(sizeKey, nextFrameId),
      };
    });
  }

  function selectMuseumLabelOption(optionId) {
    setFormState((prevState) => {
      const nextOptionId = prevState.limited_edition_museum_label === optionId ? null : optionId;

      return {
        ...prevState,
        limited_edition_museum_label: nextOptionId,
        limited_edition_museum_label_price: getMuseumLabelPrice(nextOptionId),
      };
    });
  }

  return (
    <>
      <div className="purchase-select-size__container">
        <div className="purchase-select-size__frame-header purchase-select-size__size-header">
          <span className="purchase-select-size__heading">
            Select size
          </span>
          <button
            type="button"
            className="purchase-select-size__info-trigger"
            onClick={() => setIsSizeInfoOpen(true)}
            aria-label={t('info_popups.size.aria_label')}
            aria-expanded={isSizeInfoOpen}
            aria-haspopup="dialog"
          >
            <InfoIcon className="purchase-select-size__info-icon" />
          </button>
        </div>

        {isLimitedEditionPurchase && (
          <div className="purchase-select-size__size-options">
            {Object.keys(limitedEditionOptions).map((size) => (
              isLimitedEditionAvailable(limitedEditionOptions[size]) && (
                <SizeOption
                  key={size}
                  data={limitedEditionOptions[size]}
                  isSelected={formState.limited_edition_size?.size === size}
                  selectOption={() => selectLimitedEditionSize(size, limitedEditionOptions[size])}
                />
              )
            ))}
          </div>
        )}

        <hr className="artwork-more-details__desktop__divider purchase-select-size__divider" />

        <div className="purchase-select-size__frame-options">
          <div className="purchase-select-size__frame-header">
            <span className="purchase-select-size__frame-heading">
              Framing Options
            </span>
            <button
              type="button"
              className="purchase-select-size__info-trigger"
              onClick={() => setIsFrameInfoOpen(true)}
              aria-label={t('info_popups.framing.aria_label')}
              aria-expanded={isFrameInfoOpen}
              aria-haspopup="dialog"
            >
              <InfoIcon className="purchase-select-size__info-icon" />
            </button>
          </div>

          <div className="purchase-select-size__option-list">
            {FRAME_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.id}
                className={`purchase-select-size__option-button${formState.limited_edition_frame === option.id ? ' selected' : ''}`}
                onClick={() => selectFrameOption(option.id)}
              >
                <span className="purchase-select-size__option-label">{option.label}</span>
                <span className="purchase-select-size__option-price">
                  {formatAddOnPrice(getFramePrice(normalizedSizeKey, option.id), currency, Boolean(formState.limited_edition_size))}
                </span>
              </button>
            ))}
          </div>
          <hr className="artwork-more-details__desktop__divider purchase-select-size__divider" />
        </div>

        <div className="purchase-select-size__museum-label">
          <div className="purchase-select-size__frame-header">
            <span className="purchase-select-size__frame-heading">
              Museum Label Options
            </span>
            <button
              type="button"
              className="purchase-select-size__info-trigger"
              onClick={() => setIsMuseumLabelInfoOpen(true)}
              aria-label={t('info_popups.museum_label.aria_label')}
              aria-expanded={isMuseumLabelInfoOpen}
              aria-haspopup="dialog"
            >
              <InfoIcon className="purchase-select-size__info-icon" />
            </button>
          </div>

          <div className="purchase-select-size__option-list">
            {MUSEUM_LABEL_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.id}
                className={`purchase-select-size__option-button${formState.limited_edition_museum_label === option.id ? ' selected' : ''}`}
                onClick={() => selectMuseumLabelOption(option.id)}
              >
                <span className="purchase-select-size__option-label">{option.label}</span>
                <span className="purchase-select-size__option-price">
                  {formatAddOnPrice(getMuseumLabelPrice(option.id), currency, true)}
                </span>
              </button>
            ))}
          </div>
          {/* <hr className="artwork-more-details__desktop__divider purchase-select-size__divider" /> */}
        </div>

        <Popup
          open={isSizeInfoOpen}
          close={() => setIsSizeInfoOpen(false)}
          title={t('info_popups.size.title')}
          className="generic-popup_content"
        >
          <div className="purchase-select-size__info-content">
            <p className="purchase-select-size__info-copy">
              {t('info_popups.size.description')}
            </p>
          </div>
        </Popup>

        <Popup
          open={isFrameInfoOpen}
          close={() => setIsFrameInfoOpen(false)}
          title={t('info_popups.framing.title')}
          className="generic-popup_content"
        >
          <div className="purchase-select-size__info-content">
            <p className="purchase-select-size__info-copy">
              {t('info_popups.framing.description')}
            </p>
          </div>
        </Popup>

        <Popup
          open={isMuseumLabelInfoOpen}
          close={() => setIsMuseumLabelInfoOpen(false)}
          title={t('info_popups.museum_label.title')}
          className="generic-popup_content"
        >
          <div className="purchase-select-size__info-content">
            <p className="purchase-select-size__info-copy">
              {t('info_popups.museum_label.description')}
            </p>
          </div>
        </Popup>

        {isLimitedEditionPurchase && typeof continueToShipping === 'function' && (
          <ContinueButton
            className="purchase-select-size__button"
            turnOff={!hasCompletedLimitedEditionSelection}
            onClick={continueToShipping}
          >
            Continue
          </ContinueButton>
        )}

        {isLimitedEditionPurchase && (
          <div className="purchase-select-size__limited-copy artwork-more-details__limited-copy-block">
            <h4 className="artwork-more-details__limited-heading">Limited Edition Details</h4>
            <p className="artwork-more-details__limited-copy">
              Archival inkjet on Hanhemuhle museum etching paper
            </p>
            <p className="artwork-more-details__limited-copy">
              Produced and distributed by Suarte
            </p>
            <p className="artwork-more-details__limited-copy">
              You will be reserved the lowest number available
            </p>
            <p className="artwork-more-details__limited-copy">
              Showcase it on your profile's collection
            </p>
          </div>
        )}

        {showCertificate && (
          <div className="purchase-select-size__certificate">
            <CertificateOfAuthenticityIcon className="purchase-select-size__certificate-icon" />
            <span className="purchase-select-size__certificate-text">
              Includes digital and physical Certificate of Authenticity issued by Suarte
            </span>
          </div>
        )}
      </div>
    </>
  );
}

function normalizeSizeKey(sizeKey) {
  if (!sizeKey) {
    return null;
  }

  const lowerKey = sizeKey.toLowerCase();
  if (lowerKey.includes('small')) {
    return 'small';
  }
  if (lowerKey.includes('medium')) {
    return 'medium';
  }
  if (lowerKey.includes('large')) {
    return 'large';
  }

  return lowerKey;
}

function getFramePrice(sizeKey, frameId) {
  if (!frameId || frameId === 'none') {
    return 0;
  }

  const FRAME_PRICE_BY_SIZE = {
    small: 25,
    medium: 45,
    large: 70,
  };

  return FRAME_PRICE_BY_SIZE[sizeKey] ?? 0;
}

function getMuseumLabelPrice(optionId) {
  if (!optionId || optionId === 'none') {
    return 0;
  }

  return 20;
}

function formatAddOnPrice(amount, currency, hasBaseSelection) {
  if (!hasBaseSelection) {
    return 'Select size';
  }

  if (!currency) {
    return amount > 0 ? `+$${amount}` : '+$0';
  }

  const formatted = NumberUtils.getArtworkPrice(amount, currency);
  if (amount > 0) {
    return `+${formatted}`;
  }

  const zeroFormatted = NumberUtils.getArtworkPrice(0, currency);
  return `+${zeroFormatted}`;
}
